import { useEffect, useRef, useState } from 'react';

import deleteSvg from '../../../../assets/delete.svg';
import closeSvg from '../../../../assets/close.svg';
import { ReactComponent as PlusSvg } from '../../../../assets/plus.svg';
import { MarketUnit } from './interfaces';
import DropdownField from '../../../../components/DropdownField';

const EditMarketModal: React.FC<{
  onEditCompany: (name: string, marketsUnits: MarketUnit[]) => Promise<void>;
  initialCompanyName: string;
  initialMarketsUnits: MarketUnit[];
  markets: string[];
  onClose: () => void;
}> = ({ onClose, markets, onEditCompany, initialCompanyName, initialMarketsUnits }) => {
  const [marketUnits, setMarketUnits] = useState<MarketUnit[]>(initialMarketsUnits);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleAddMarket = () => {
    setMarketUnits([...marketUnits, { market: markets[0], units: 0 }]);
  };

  const handleRemoveMarket = (index: number) => {
    const updatedMarketUnits = marketUnits.filter((_, i) => i !== index);
    setMarketUnits(updatedMarketUnits);
  };

  const handleMarketChange = (index: number, value: string) => {
    const updatedMarketUnits = [...marketUnits];
    updatedMarketUnits[index].market = value;
    setMarketUnits(updatedMarketUnits);
  };

  const handleUnitsChange = (index: number, value: number) => {
    const updatedMarketUnits = [...marketUnits];
    updatedMarketUnits[index].units = value;
    setMarketUnits(updatedMarketUnits);
  };

  const getAlert = (): string => {
    if (marketUnits.length === 0) {
      return 'Please add at least one market/unit!';
    }

    for (const marketUnit of marketUnits) {
      if (marketUnit.market === '' || marketUnit.units === 0) {
        return 'Please fill in all markets and units!';
      }
    }

    const uniqueMarkets = marketUnits.reduce((unique, current) => {
      if (!unique.some(marketUnit => marketUnit.market === current.market)) {
        return [...unique, current];
      }
      return unique;
    }, [] as MarketUnit[]);

    if (uniqueMarkets.length !== marketUnits.length) {
      return "Please don't add the same market twice!";
    }

    return '';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(16, 29, 59, 0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div style={styles.modalContent} ref={modalRef} onClick={event => event.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              fontWeight: '700',
              fontSize: '2.4vh',
            }}
          >
            {initialCompanyName}
          </div>

          <button
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            <img src={closeSvg} alt="logout" style={{ width: '2.4vh', height: '2.4vh' }} />
          </button>
        </div>
        <div style={{ minHeight: '1.6vh' }} />
        <div
          style={{
            fontWeight: '700',
            fontSize: '1.6vh',
          }}
        >
          Markets/Units
        </div>
        <div style={{ minHeight: '0.8vh' }} />

        {marketUnits.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0.8vh',
              width: '100%',
              marginBottom: '0.8vh',
            }}
          >
            <DropdownField
              values={markets}
              onUpdate={name => handleMarketChange(index, name)}
              initialValue={item.market}
              allowFilter={false}
            />

            <input
              type="number"
              placeholder="Enter amount of units..."
              value={item.units}
              style={{
                padding: '1.6vh',
                fontSize: '1.4vh',
                borderRadius: '1.6vh',
                border: '0.1vh solid rgba(16, 29, 59, 0.2)',
                backgroundColor: '#ffffff',
                color: '#101D3B',
                cursor: 'pointer',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box' as 'border-box',
              }}
              onChange={e => handleUnitsChange(index, parseInt(e.target.value))}
            />
            <button
              onClick={() => handleRemoveMarket(index)}
              style={{
                minWidth: '5.2vh',
                height: '5.2vh',
                backgroundColor: 'rgba(243, 119, 102, 0.05)',
                borderRadius: '1.6vh',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <img src={deleteSvg} alt="logout" style={{ width: '2.4vh', height: '2.4vh' }} />
            </button>
          </div>
        ))}
        <button
          style={{
            width: '100%',
            minHeight: '5.6vh',
            color: '#00AB9B',
            backgroundColor: 'white',
            border: '0.2vh solid #00AB9B',
            fontSize: '1.6vh',
            fontWeight: '700',
            borderRadius: '1.6vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleAddMarket}
        >
          <PlusSvg fill="#00AB9B" height={'2.4vh'} width={'2.4vh'} />
          <div style={{ width: '0.8vh' }} />
          Add another market
        </button>
        <div style={{ minHeight: '1.6vh' }} />
        {getAlert() !== '' ? (
          <div
            style={{
              width: '100%',
              minHeight: '5.6vh',
              color: 'rgba(16, 29, 59, 0.4)',
              backgroundColor: 'rgba(16, 29, 59, 0.05)',
              fontSize: '1.6vh',
              fontWeight: '700',
              borderRadius: '1.6vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getAlert()}
          </div>
        ) : (
          <button
            style={{
              width: '100%',
              minHeight: '5.6vh',
              color: 'white',
              backgroundColor: 'rgba(0, 171, 155, 1)',
              fontSize: '1.6vh',
              fontWeight: '700',
              borderRadius: '1.6vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
            }}
            onClick={() => onEditCompany(initialCompanyName, marketUnits)}
          >
            Save Changes
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  modalContent: {
    backgroundColor: 'white',
    padding: '2.4vh',
    borderRadius: '1.6vh',
    width: '52vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    color: 'rgba(16, 29, 59, 1)',
    maxHeight: 'calc(100vh - 6.4vh)',
    overflowY: 'auto' as 'auto',
  },
  container: {
    width: '100%',
    display: 'flex',
  },
  select: {
    padding: '1.2vh',
    borderRadius: '1.6vh',
    border: '0.1vh solid rgba(16, 29, 59, 0.2)',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as 'border-box',
  },
  input: {
    fontSize: '1.4vh',
    fontWeight: '400',
    border: 'none',
    backgroundColor: '#ffffff',
    color: 'rgba(16, 29, 59, 1)',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
  },
  dropdown: {
    position: 'absolute' as 'absolute',
    padding: '0vh 1.6vh',
    top: 'calc(100% - 0.6vh)', // Position it right below the input
    left: '0',
    width: 'calc(100% - 3.2vh)', //compensate padding
    borderTop: 'none',
    maxHeight: '15vh',
    overflowY: 'auto' as 'auto',
    backgroundColor: '#ffffff',
    zIndex: 1,
    borderRadius: '1.6vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
  },
  option: {
    padding: '0.8vh 0vh',
    fontWeight: '400',
    fontSize: '1.4vh',
    cursor: 'pointer',
    borderBottom: '0.1vh solid #eee',
    color: '#2d3436',
    textAlign: 'left' as 'left',
  },
  scrollbar: {
    // Empty for now due to different browser specification complexity
  },
};

export default EditMarketModal;
